import { useState, useMemo, useEffect } from "react";
import "./Sidebar.css";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListUl, faChevronDown, faXmark, faRightFromBracket } from "@fortawesome/free-solid-svg-icons";
import Item from "./item";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LogOut from "../../Component/LogOut/LogOut";
import imgUser from "../../Image/team-01.png";
import { useSidebar } from "../../Context/SidebarContext";
import { getDriveImageUrl } from "../../util/Driveimage";
import { clearUserSession, getUserSession } from "../../function/AuthStorage";
import { useAccess } from "../../permissions/access";
import { allowedGroups, groupOf, navItem } from "../../navigation/navItems";
import { canOpen } from "../../permissions/registry";
import logo from "../../assets/fLogo.png";
// القائمة الموحّدة لكل المستخدمين: العناصر والمجموعات في navigation/navItems.js،
// وما يظهر منها يحدده شرط المسار (canOpen) لا نوع الحساب.

// مهم: المكوّنان معرّفان خارج Sidebar حتى تبقى هويتهما ثابتة بين مرات الرسم
// (تعريفهما داخله كان يعيد تركيب القائمة في كل رسم فتهتز).
function GroupHeader({ title, group, icon, open, isGroupOpen, onToggle }) {
  return (
    <motion.button
      type="button"
      onClick={() => onToggle(group)}
      whileTap={{ scale: 0.98 }}
      aria-expanded={isGroupOpen}
      className={`asf-side-group ${isGroupOpen ? "asf-side-group--open" : ""} group relative w-full flex items-center ${open ? "justify-between" : "justify-center"} gap-2 px-2 py-2 mb-1 rounded-lg text-white/75 hover:text-white transition-colors duration-200 cursor-pointer shrink-0`}
    >
      <div className={`flex items-center gap-3 ${!open ? "justify-center w-full" : ""}`}>
        <div className="w-8 h-8 min-w-[32px] flex items-center justify-center shrink-0 rounded-md text-white/60">
          <i className={`${icon} text-[14px]`} />
        </div>

        {open && (
          <span className="asf-side-group__label flex-1 min-w-0 overflow-hidden text-ellipsis text-[13px] font-medium font-cairo whitespace-nowrap leading-normal">
            {title}
          </span>
        )}
      </div>

      {open && (
        <motion.div
          animate={{ rotate: isGroupOpen ? 180 : 0 }}
          transition={{ duration: 0.18 }}
          className="w-5 h-5 min-w-[20px] flex items-center justify-center shrink-0 text-white/35 group-hover:text-white/70"
        >
          <FontAwesomeIcon icon={faChevronDown} className="text-[9px]" />
        </motion.div>
      )}
    </motion.button>
  );
}

function GroupItems({ items, show, label }) {
  return (
    <AnimatePresence initial={false}>
      {show && items?.length > 0 && (
        <motion.div
          key="items"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="overflow-hidden"
        >
          <div className="asf-side-sub relative mr-4 pr-3 pl-1 py-1 mb-2">
            <span className="absolute right-0 top-1 bottom-1 w-px bg-white/[0.10]" />

            <div className="flex flex-col gap-0.5">
              {items.map((item) => (
                <div key={item.id} className="w-full min-w-0">
                  <Item id={item.id} icon={item.icon} name={label(item)} link={item.link} />
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Sidebar() {
  const navigate = useNavigate();
  const { open: openState, toggle, setOpen, mode, overlay } = useSidebar();
  // على الجوال القائمة إمّا مخفية كلياً أو ظاهرة كاملة بأسمائها
  const open = mode === "mobile" ? true : openState;
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);

  const cookieValue = useMemo(() => getUserSession() || {}, []);
  const location = useLocation();
  const { i18n } = useTranslation();
  const en = String(i18n.language || "ar").startsWith("en");
  const label = (item) => (en && item.nameEn) || item.name;
  // يعيد الرسم عند تحديث الصلاحيات من الخلفية
  const { permissions: userPerms, isSuperAdmin } = useAccess();

  const userImageSrc = useMemo(() => getDriveImageUrl(cookieValue.userImage) || imgUser, [cookieValue.userImage]);

  const functionLogout = () => {
    clearUserSession();
    localStorage.clear();
    navigate("/");
  };

  const groups = useMemo(() => allowedGroups(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userPerms, isSuperAdmin]);

  // مراجعة 2026-10-15: قسم «الرئيسية» (تحديث 60) يضم الرئيسية واللوحة والمساعد — فلا يُكرر رابط «الرئيسية» المثبت فوقه.
  // إن لم يبقَ في القسم غير «الرئيسية» (مستخدم محدود) يظهر الرابط المثبت وحده كما كان.
  const homeGroup = groups.find((g) => g.key === "home");
  const homeItem = !homeGroup || homeGroup.items.length <= 1 ? navItem("home") : null;
  const shownGroups = homeItem ? groups.filter((g) => g.key !== "home") : groups;
  const profileItem = canOpen("/profile") ? navItem("profile") : null;

  // المجموعة التي فيها الصفحة الحالية تُفتح وحدها (Accordion: مجموعة واحدة مفتوحة في كل وقت)
  useEffect(() => {
    const g = groupOf(location.pathname);
    if (g) setOpenGroup(g);
  }, [location.pathname]);

  const toggleGroup = (group) => setOpenGroup((prev) => (prev === group ? null : group));

  return (
    <>
      {overlay && openState && (
        <div className="asf-sidebar-backdrop" onClick={() => setOpen(false)} aria-hidden="true" data-testid="sidebar-backdrop" />
      )}
      <aside
        className={`asf-sidebar asf-sidebar--${mode} ${openState ? "is-open" : "is-closed"}`}
        data-open={openState}
        data-mode={mode}
        aria-label={en ? "Main menu" : "القائمة الرئيسية"}
        data-testid="app-sidebar"
      >
        <div className="asf-sidebar__panel flex flex-col w-full h-full box-border m-0 bg-mainColor text-white border border-white/20 overflow-hidden">

         <div className="asf-sidebar__header shrink-0">
         <div className="asf-sidebar__top">
  <div className="asf-brand" data-open={open}>
    <div className="asf-brand__ring">
      <div className="asf-brand__logo">
        <img src={logo} alt={en ? "Logo" : "الشعار"} draggable={false} />
      </div>
    </div>

    {open && (
      <div className="asf-brand__text">
        <span className="asf-brand__name">{en ? "ASF" : "عصف"}</span>
        <span className="asf-brand__tag">{en ? "Control panel" : "لوحة التحكم"}</span>
      </div>
    )}
  </div>

  <button
    type="button"
    onClick={toggle}
    className="asf-sidebar__toggle"
    aria-label={en ? "Toggle menu" : "طي/فتح القائمة"}
    aria-expanded={openState}
    data-testid="sidebar-toggle"
  >
    <FontAwesomeIcon icon={mode === "mobile" ? faXmark : faListUl} />
  </button>
</div>

  <div className="asf-sidebar__divider" />
</div>

          <div className="asf-side-scroll flex flex-col w-full flex-1 min-h-0 overflow-y-auto overflow-x-hidden">

            {homeItem && (
              <div className="mb-2 shrink-0">
                <Item id={homeItem.id} icon={homeItem.icon} name={label(homeItem)} link={homeItem.link} />
              </div>
            )}

            {shownGroups.map((g) => (
              <div key={g.key} className="mb-1 shrink-0" data-testid={`side-group-${g.key}`}>
                <GroupHeader
                  title={(en && g.titleEn) || g.title}
                  group={g.key}
                  icon={g.icon}
                  open={open}
                  isGroupOpen={openGroup === g.key}
                  onToggle={toggleGroup}
                />
                <GroupItems items={g.items} show={open && openGroup === g.key} label={label} />
              </div>
            ))}

            {profileItem && (
              <div className="mt-1 shrink-0">
                <Item id={profileItem.id} icon={profileItem.icon} name={label(profileItem)} link={profileItem.link} />
              </div>
            )}
          </div>

          {cookieValue && (
            <motion.div className={`bg-white text-mainColor rounded-xl transition-all duration-300 w-full shrink-0 mt-2 flex items-center ${open ? "p-3 opacity-100" : "p-1.5 opacity-0 pointer-events-none h-0 overflow-hidden"}`}>
              <img src={userImageSrc} alt="User" onError={(e) => { e.currentTarget.src = imgUser; }} className="w-10 h-10 rounded-full mr-2.5 shrink-0 object-cover" />

              <div className="text-right whitespace-normal break-words min-w-0 flex-1">
                <h4 className="m-0 text-sm whitespace-normal break-words">{cookieValue.userName}</h4>
                <p className="m-0 text-xs text-gray-500">{cookieValue.email}</p>
              </div>
            </motion.div>
          )}

          {cookieValue && (
            <button
              type="button"
              onClick={() => setLogoutDialogOpen(true)}
              title={en ? "Log out" : "تسجيل الخروج"}
              aria-label={en ? "Log out" : "تسجيل الخروج"}
              className={`bg-white/10 text-white border border-white/20 rounded-md cursor-pointer transition-colors duration-300 hover:bg-secondaryColor shrink-0 w-full mt-2 min-h-[var(--asf-tap)] text-[length:var(--asf-text-sm)] ${open ? "px-4 py-2" : "px-1 py-2"}`}
              data-testid="sidebar-logout"
            >
              {open ? (en ? "Log out" : "تسجيل الخروج") : <FontAwesomeIcon icon={faRightFromBracket} />}
            </button>
          )}
        </div>
      </aside>

      <LogOut open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)} onConfirm={functionLogout} />
    </>
  );
}

export default Sidebar;
